/**
 * Shared formatting + small primitives for the workshop detail edit sections
 * (BON-117). Mirrors the venue detail's field helpers; the controlled
 * Input/Select wrappers and the `useFormDraft` hook are reused from the venue
 * detail folder rather than duplicated.
 */
import type { CSSProperties } from 'react';

import { crewPositionLabel } from '../../../../lib/crewPositions';
import { formatVenueDate, parseVenueLocal, venueWallClock } from '../../../../utils/venueTime';

/** The small caps heading used for each section inside a detail card
 *  ("BASICS", "BOOKINGS", "ROSTER", "RESERVATIONS"). Shared so a new section
 *  matches its neighbours by construction rather than by copying five numbers. */
export const labelStyle: CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '0.08em',
  fontWeight: 600,
  color: 'var(--nb-subtle)',
};

/** Cents → "$65" (whole dollars) or "$64.50" when there's a remainder. */
export function formatPrice(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

/** Parse a "$65" / "65" / "64.50" price input into integer cents, or null. */
export function parsePriceToCents(input: string): number | null {
  const cleaned = input.replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}

/**
 * Workshop-time display formatters (BON-386). The input is the venue-local wall
 * clock (`localStartDatetime` / `localEndDatetime`) — a naive ISO with no `Z` —
 * rendered with NO timezone conversion via `utils/venueTime`. Never pass the
 * absolute `startsAt`/`endsAt` here; those are for sort/compare only. A null
 * value (unpopulated local column) renders as "—".
 */
export function formatDate(local: string | null): string {
  return formatVenueDate(local, 'EEE, MMM d');
}

/** Venue-local wall clock → "6:30p" (single-letter am/pm, minutes only when
 *  non-zero). */
export function formatTime(local: string | null): string {
  const t = venueWallClock(local);
  if (!t) return '—';
  const ampm = t.hours >= 12 ? 'p' : 'a';
  const h12 = t.hours % 12 === 0 ? 12 : t.hours % 12;
  return t.minutes === 0 ? `${h12}${ampm}` : `${h12}:${String(t.minutes).padStart(2, '0')}${ampm}`;
}

/** Bare-dollars text for the editable price input (no "$"): cents → "65" or
 *  "64.50". The "$" is rendered as a static adornment, not stored in the value. */
export function priceInputValue(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
}

/** English ordinal suffix for a day-of-month (1 → "st", 2 → "nd", …). */
function ordinal(day: number): string {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/** Venue-local wall clock → "6pm" / "6:30pm" (lowercase am/pm, minutes only when
 *  non-zero). Used by the breadcrumb's date/time segment. */
export function formatClock(local: string | null): string {
  const t = venueWallClock(local);
  if (!t) return '—';
  const ampm = t.hours >= 12 ? 'pm' : 'am';
  const h12 = t.hours % 12 === 0 ? 12 : t.hours % 12;
  return t.minutes === 0 ? `${h12}${ampm}` : `${h12}:${String(t.minutes).padStart(2, '0')}${ampm}`;
}

/**
 * Human-readable duration between two ISO instants (BON-215), e.g. "2h",
 * 90 minutes → "1h 30m", "45m". Rounds to whole minutes; a non-positive span
 * renders as "—" (a bad range is surfaced as a readiness blocker, not here).
 */
export function formatDuration(startLocal: string | null, endLocal: string | null): string {
  if (!startLocal || !endLocal) return '—';
  const ms = parseVenueLocal(endLocal).getTime() - parseVenueLocal(startLocal).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const totalMinutes = Math.round(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

/** Venue-local wall clock → "Fri, Oct 1st @ 6:30pm" — the breadcrumb date/time
 *  segment. */
export function formatBreadcrumbDateTime(local: string | null): string {
  if (!local) return '—';
  const day = parseVenueLocal(local).getDate();
  const weekday = formatVenueDate(local, 'EEE');
  const month = formatVenueDate(local, 'MMM');
  return `${weekday}, ${month} ${day}${ordinal(day)} @ ${formatClock(local)}`;
}

// ─── Audit log formatters (BON-201) ──────────────────────────────────────────

/** Convert a snake_case or camelCase field name to a human-readable label. */
export function auditFieldLabel(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * Display label for a crew position.
 *
 * BON-420 — a lookup against `lib/crewPositions` rather than the old
 * underscore-to-space transform. The transform happened to render the three
 * current positions acceptably, but it derived text instead of reading it, so a
 * position whose label isn't just its snake_case value would have rendered
 * wrong here while reading correctly everywhere else.
 */
export function formatCrewPosition(position: string): string {
  return crewPositionLabel(position);
}

/**
 * Format a scalar audit value for display.
 * - priceCents: cents number → "$65"
 * - capacity: number → string as-is
 * - status/lifecycle: string → Title Case
 * - unknown: JSON fallback
 */
export function formatAuditScalar(field: string, value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (field === 'priceCents' && typeof value === 'number') {
    return formatPrice(value);
  }
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    // Title-case enum values (status, lifecycle, and similar enum fields)
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  return JSON.stringify(value);
}

/**
 * ISO → "May 20" short date label, UTC.
 *
 * DELIBERATELY UTC, NOT venue-local (BON-386): the audit surface renders two
 * kinds of value through this, both correct under UTC —
 *   • `changedAt`, the absolute instant an edit happened (an event timestamp,
 *     not a workshop wall clock); and
 *   • the before/after of a `startsAt`/`endsAt` schedule change, which the
 *     backend serializes from the naive `localStartsAt` Date — a wall clock
 *     tagged `Z`, so UTC components ARE the wall clock.
 * These do not consume `localStartDatetime`, so the venue-local formatters
 * above don't apply.
 */
export function formatAuditDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    // eslint-disable-next-line no-restricted-syntax -- audit event timestamp / Z-tagged audit value, not a venue-local field (see doc above)
    timeZone: 'UTC',
  });
}

/**
 * ISO → "3:12p" clock label, UTC. Same UTC rationale as {@link formatAuditDate}
 * — decoupled from the venue-local `formatTime` so the audit surface keeps its
 * UTC semantics.
 */
export function formatAuditTime(iso: string): string {
  const d = new Date(iso);
  /* eslint-disable no-restricted-syntax -- audit event timestamp / Z-tagged audit value, not a venue-local field (see formatAuditDate) */
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  /* eslint-enable no-restricted-syntax -- end audit UTC block */
  const ampm = h >= 12 ? 'p' : 'a';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
}
